import React, { createContext, useState, ReactNode, useContext, useEffect, Dispatch, SetStateAction } from "react";
import { useParams } from "react-router-dom";
import { Hooks } from 'hooks/commonHooks';
import { MessageType } from 'models/BaseModel';
import { Code } from "helpers/CalendarImports";

// Define the context properties
interface PrintContextProps {
  keyId: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  showMenu: boolean;
  setShowMenu: (showMenu: boolean) => void;
  message: MessageType;
  setMessage: Dispatch<SetStateAction<MessageType>>;
  error: string | null;
  setError: (error: string | null) => void;
  menuPosition: any;
  setMenuPosition: any;
  iframeRef: any;
  contextMenuRef: any;
}

// Create the context
const PrintContext = createContext<PrintContextProps | undefined>(undefined);

// Provider Component
export const PrintProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();

  // Initialize state
  const [keyId, setKeyId] = useState<string>(paramKeyId || "");
  const [loading, setLoading] = useState<boolean>(true);
  const [showMenu, setShowMenu] = useState<boolean>(false); 
  const [message, setMessage] = useState<MessageType>(Code.Message.None);
  const [error, setError] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = Hooks.Form.Position(); 

  const iframeRef = Hooks.Form.Frame();
  const contextMenuRef = Hooks.Form.Div();

  useEffect(() => {
    if (paramKeyId) {
      setKeyId(paramKeyId);
    }
  }, [paramKeyId]);

  return (
    <PrintContext.Provider value={{ 
      keyId, 
      loading, setLoading, 
      showMenu, setShowMenu,
      message, setMessage,  
      error, setError, 
      menuPosition, setMenuPosition, 
      iframeRef, 
      contextMenuRef 
    }}>
      {children}
    </PrintContext.Provider>
  );
};

// Custom Hook for using the context
export const usePrintContext = () => {
  const context = useContext(PrintContext);
  if (!context) {
    throw new Error("usePrintContext must be used within a PrintProvider");
  }
  return context;
};
